import { get } from '@shared/adapters/lodash'
import {
  ACCOUNT_STATE,
  type AccountStateValue,
  type PausedReasonValue,
} from '../constants/accountState'
import type { AccountStatus, KycDetails, KycStatus, UserProfile } from '../types/profileTypes'

export const parseUserProfile = (resp: unknown): UserProfile => ({
  userId: get(resp, 'data.userId') || '',
  firstName: get(resp, 'data.firstName') || '',
  lastName: get(resp, 'data.lastName') || '',
  legalName: get(resp, 'data.legalName') ?? null,
  email: get(resp, 'data.email') || '',
  mobileNumber: get(resp, 'data.mobileNumber') || '',
})

export const parseAccountStatus = (resp: unknown): AccountStatus => {
  const bankAccountRaw = get(resp, 'data.bankAccount')
  return {
    onboardingStatus: get(resp, 'data.onboardingStatus') || '',
    kycStatus: get(resp, 'data.kycStatus') || '',
    // investorStatus drives the dashboard state machine + the access-restriction
    // gate (blocked → contact-support blockers). Always present on the wire.
    investorStatus: get(resp, 'data.investorStatus') || '',
    // Server-owned screen discriminator. Cast at the parser seam; the dashboard
    // resolver has a safe default for any unexpected value.
    accountState:
      (get(resp, 'data.accountState') as AccountStateValue | undefined) || ACCOUNT_STATE.IN_REVIEW,
    pausedReason: (get(resp, 'data.pausedReason') as PausedReasonValue | null | undefined) ?? null,
    accessRestricted: Boolean(get(resp, 'data.accessRestricted')),
    // False default is the safe direction — a missing field surfaces the
    // "complete your risk profile" nudge rather than hiding it.
    profileCompleted: Boolean(get(resp, 'data.profileCompleted')),
    profileCompletionPercentage: get(resp, 'data.profileCompletionPercentage') ?? 0,
    bankAccount: bankAccountRaw
      ? {
          id: get(bankAccountRaw, 'id') || '',
          accountHolderName: get(bankAccountRaw, 'accountHolderName') || '',
          bankName: get(bankAccountRaw, 'bankName') || '',
          accountStatus: get(bankAccountRaw, 'accountStatus') || '',
        }
      : null,
  }
}

interface RawKycDocument {
  documentTitle?: string | null
  filePath?: string | null
}

export const parseKycDetails = (resp: unknown): KycDetails => {
  const documentsRaw = get(resp, 'data.documents')
  const documents = Array.isArray(documentsRaw)
    ? (documentsRaw as RawKycDocument[]).map((doc) => ({
        documentTitle: doc.documentTitle ?? '',
        filePath: doc.filePath ?? '',
      }))
    : []
  return {
    kycStatus: get(resp, 'data.kycStatus') || '',
    submittedAt: get(resp, 'data.submittedAt') ?? null,
    documents,
  }
}

export const parseKycStatus = (resp: unknown): KycStatus => ({
  submissionId: get(resp, 'data.submissionId') ?? null,
  kycStatus: get(resp, 'data.kycStatus') ?? null,
  kycVendor: get(resp, 'data.kycVendor') ?? null,
  submittedAt: get(resp, 'data.submittedAt') ?? null,
})
